import { useDateFormatter, useTranslations } from "../../contexts/Translation";
import { H2, H3 } from "../Headers/Headings";
import Section from "../Section";
import { useEffect, useMemo, useState } from "react";
import { useApiData } from "../../contexts/ApiData";
import Button from "../Button/Button";
import { Card, Recommendation, Survey, SurveyFilterType, ValidationError, ValidationResult } from "../../types";
import ErrorText from "./ErrorText";
import FeedbachCard from "@/components/JourneyCard/JourneyCardView";
import { useNavigate } from "react-router-dom";
import Input from "./Input";
import { InfoMessage } from "../InfoMessage/InfoMessage";
import SurveyTypeFilter from "../SurveyFilter/SurveyFilter";
import CardStats from "@/components/JourneyCard/CardStats";
import RecommendationPicker from "./RecommendationPicker";
import GroupAccess from "./GroupAccess";
import styles from "./FormStyles.module.css";
import cardStyles from "./CardForm.module.css";

// Mirrors MAX_CARD_RECOMMENDATIONS in the server's card domain.
const MAX_RECOMMENDATIONS = 3;

const CardForm = () => {
  const t = useTranslations("cardForm");
  const d = useDateFormatter();
  const { post, get } = useApiData();
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [selected, setSelected] = useState<Survey[]>([]);
  const [previewCardData, setPreviewCardData] = useState<Card | undefined>();
  const [imageBase64, setImageBase64] = useState<string | null>("");
  const [hasPreview, setHasPreview] = useState(false);
  const navigate = useNavigate();
  const [cardTitle, setCardTitle] = useState("");
  const [cardTitlePreview, setCardTitlePreview] = useState("");
  const [cardId] = useState(crypto.randomUUID());
  const [exposeDomains, setExposeDomains] = useState<boolean>(true);
  const [recommendationIds, setRecommendationIds] = useState<string[]>([]);
  // No groups means the whole organisation sees the card under shared cards.
  const [groupsWithAccess, setGroupsWithAccess] = useState<string[]>([]);

  const [filteredSurveys, setFilteredSurveys] = useState(surveys);

  const [selectedFilter, setSelectedFilter] = useState<SurveyFilterType>("all");
  const [isFilteringDisabled, setIsFilteringDisabled] = useState(false);

  const handleFilterChange = (filterType: SurveyFilterType) => {
    setSelectedFilter(filterType);
  };

  useEffect(() => {
    const call = async () => {
      const surveyList = await get("/card/surveys");
      setSurveys(surveyList);
      // setSurveys(surveyList1.filter((s: Survey) => s.status === 'finished'));
    };
    call();
  }, [get]);

  useEffect(() => {
    if (selectedFilter === "all") {
      setFilteredSurveys(surveys);
    } else {
      setFilteredSurveys(surveys.filter((s) => s.surveyType === selectedFilter));
    }
  }, [selectedFilter, surveys]);

  useEffect(() => {
    const call = async () => {
      const previewCard = await get(`/card/preview?from=${selected[0].id}&to=${selected[1].id}&surveyType=${selectedFilter}`);
      setPreviewCardData(previewCard);
      // The available recommendations follow the interval, so a changed interval clears the pick.
      setRecommendationIds([]);
      setHasPreview(true);
    };
    if (selected.length === 2) {
      call();
    }
  }, [get, selected, selectedFilter]);

  const validateData = (): ValidationResult => {
    const errors = [];

    if (selected.length !== 2) {
      errors.push({ name: "form", message: t("invervalError") });
    }

    return { errors: errors.length > 0 ? errors : undefined, success: errors.length === 0 };
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCardTitle(e.target.value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationResult = validateData();

    if (validationResult.success && !imageBase64) {
      setErrors([{ name: "form", message: t("imageNotReady") }]);
    } else if (validationResult.success) {
      const formData = {
        from: selected[0].id,
        to: selected[1].id,
        cardTitle,
        image: imageBase64,
        surveyType: selected[0].surveyType,
        exposeDomains,
        recommendationIds,
        groupsWithAccess,
      };
      const result = await post(`/card/${cardId}`, formData);
      if (!result) {
        setErrors([{ name: "form", message: t("serverError") }]);
      } else {
        navigate(`/cards`);
      }
    } else {
      setErrors(validationResult.errors || []);
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  const availableRecommendations: Recommendation[] = (previewCardData as any)?.availableRecommendations || [];
  // The preview takes a new image of the card each time this object changes, so it may only
  // change when the card does: not on each keystroke, and not when the image comes back.
  const previewCard = useMemo(() => {
    if (!previewCardData) return undefined;
    const available: Recommendation[] = (previewCardData as any).availableRecommendations || [];
    const recommendations = recommendationIds.map((id) => available.find((r) => r.id === id)).filter(Boolean) as Recommendation[];
    return { ...previewCardData, cardId, cardTitle: cardTitlePreview, recommendations } as Card;
  }, [previewCardData, cardId, cardTitlePreview, recommendationIds]);

  const byStart = (a: Survey, b: Survey) => (a.startDate > b.startDate ? 1 : a.startDate < b.startDate ? -1 : 0);
  const first = selected[0];
  const last = selected[1];
  const firstOptions = [...filteredSurveys].sort(byStart);
  const lastOptions = first ? surveys.filter((s) => s.surveyType === first.surveyType && s.startDate >= first.startDate).sort(byStart) : [];
  // Same rule as getSurveyIdsBetween on the server: same type, starting between the first and the last.
  const range = first && last ? lastOptions.filter((s) => s.startDate <= last.startDate) : [];

  const handleFirstChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setErrors([]);
    const survey = surveys.find((s) => s.id === e.target.value);
    if (!survey) {
      setSelected([]);
      setIsFilteringDisabled(false);
      return;
    }
    setSelectedFilter(survey.surveyType || "long-term");
    setIsFilteringDisabled(true);
    const keepLast = last && last.surveyType === survey.surveyType && last.startDate >= survey.startDate;
    setSelected(keepLast ? [survey, last] : [survey]);
  };

  const handleLastChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setErrors([]);
    const survey = surveys.find((s) => s.id === e.target.value);
    setSelected(survey ? [first, survey] : [first]);
  };

  const surveyLabel = (survey: Survey) => `${survey.surveyName || t("survey")} ${d(survey.startDate)}-${d(survey.endDate)}`;

    return (
    <Section span={8}>
      <H2>{t("heading")}</H2>
      {surveys && surveys.length === 0 ? (
        <InfoMessage>{t("noSurveys")}</InfoMessage>
      ) : (
        <form className={styles.form} onSubmit={handleSubmit}>
          <SurveyTypeFilter surveys={surveys} onFilterChange={handleFilterChange} selectedFilter={selectedFilter} isDisabled={isFilteringDisabled} />
          {errors.filter((e) => e.name !== "form").length > 0 && <ErrorText variant='form' text={t("formErrors")} />}
          <ErrorText variant='form' error={errors.find((e) => e.name === "form")} />
          <p>{t("selectSurveyInfo")}</p>
          <div className={styles.fieldRow}>
            <label className={cardStyles.picker}>
              <span>{t("firstSurvey")}</span>
              <select name='firstSurvey' value={first?.id || ""} onChange={handleFirstChange}>
                <option value=''>{t("chooseSurvey")}</option>
                {firstOptions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {surveyLabel(s)}
                  </option>
                ))}
              </select>
            </label>
            <label className={cardStyles.picker}>
              <span>{t("lastSurvey")}</span>
              <select name='lastSurvey' value={last?.id || ""} onChange={handleLastChange} disabled={!first}>
                <option value=''>{t("chooseSurvey")}</option>
                {lastOptions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {surveyLabel(s)}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {range.length > 0 && (
            <div className={cardStyles.range} aria-label={t("rangeLabel")} role='group'>
              <svg className={cardStyles.journey} viewBox={`0 0 ${Math.max(range.length - 1, 1) * 40 + 24} 24`} aria-hidden='true' data-testid='card-range-line'>
                <line x1='12' y1='12' x2={Math.max(range.length - 1, 1) * 40 + 12} y2='12' />
                {range.map((s, i) => (
                  <circle key={s.id} data-testid='card-range-dot' cx={12 + i * 40} cy='12' r={i === 0 || i === range.length - 1 ? 8 : 6} />
                ))}
              </svg>
              <p className={cardStyles.rangeSummary}>{t("rangeSummary", range.length, previewCardData?.responders ?? "-")}</p>
              <p>{t("rangeAllIncluded")}</p>
            </div>
          )}
          <div className={styles.fieldRow}>
            <Input
              error={errors.find((e) => e.name === "cardTitle")}
              name='cardTitle'
              handleChange={handleChange}
              label={t("cardTitle")}
              value={cardTitle}
              handleBlur={() => setCardTitlePreview(cardTitle)}
            />
          </div>

          <div className={styles.fieldRow}>
            <Input
              type='checkbox'
              name='exposeDomains'
              label={t("exposeDomains")}
              value={exposeDomains}
              handleChange={(e: any) => setExposeDomains(e.target.checked)}
              helpText={t("help.exposeDomains")}
            />
          </div>

          <GroupAccess
            initiallySelected={groupsWithAccess}
            setGroupsWithAccess={setGroupsWithAccess}
            heading={<H3>{t("groupsHeading")}</H3>}
            helpText={<p className={cardStyles.groupsHelp}>{t("groupsHelp")}</p>}
          />

          {hasPreview && previewCardData && (
            <>
              <RecommendationPicker
                available={availableRecommendations}
                selectedIds={recommendationIds}
                max={MAX_RECOMMENDATIONS}
                onChange={setRecommendationIds}
              />
              <H3>{t("preview")}</H3>
              <FeedbachCard setImage={setImageBase64} isPreview card={previewCard as Card} />
              <CardStats card={previewCard as Card} />
              {!exposeDomains && <div className={styles.note}>{/* Informative note in preview that domains will be hidden publicly */}</div>}
            </>
          )}
          <div className={styles.actions}>
            {/* The preview screenshot is the card image, so wait until it is taken */}
            <Button disabled={hasPreview && !imageBase64}>{t("submit")}</Button>
          </div>
        </form>
      )}
    </Section>
  );
};

export default CardForm;
